import { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Text, TextButton } from '@zillow/constellation-10';

import {
  DisplayTimeZones,
  DisplayTourTypes,
  EditListingTourConfigSteps,
  TOUR_DURATION_DISPLAY_MAP,
} from '@zg-rentals/touring';

import { useTourSchedulingContext } from '../../../../../../contexts/TourSchedulingContext';
import { useGetListingDetails, useGetListingRecommendations } from '../../../../../../hooks/queries/properties';

import { REVIEW_PAGE_SECTIONS } from '../../../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../../../constants/RecommendationsConstants';
import { getTourCalendarRoute } from '../../../../../../constants/urls';

import AccordionIconError from '../../components/AccordionIconError';
import ContentSectionEditButton from '../../components/ContentSectionEditButton';
import FieldValueDisplay from '../../components/FieldValueDisplay';
import TouringAvailability from '../../components/TouringSection/TouringAvailability';

import {
  iconPlaceholderStyles,
  reviewSectionStyles,
  reviewSectionTitleStyles,
  reviewTitleRowStyles,
} from '../../styles';

import {
  emitReviewStepBookingMethodInitialStateImpressionEvent,
  EVENT_IDS,
} from '../../../../../../analytics/events/touringModularListing';
import { useAuth } from '../../../../../AuthProvider';
import RecommendationIconBulb from '../../../../../PostingPath/RecommendationIconBulb';

import type { TimeZoneIds } from '@zg-rentals/touring';

const TOUR_BOOKING_METHOD = 'tour-booking-method';

type TouringSectionProps = {
  onClickEditTourSection: (modalStep: EditListingTourConfigSteps) => void;
  onClickRecommendation: (recommendationType: string) => void;
};

const TouringSection = ({ onClickEditTourSection, onClickRecommendation }: TouringSectionProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const active = listingDetails?.active ?? false;
  const recommendations = recommendationsBySection[REVIEW_PAGE_SECTIONS.CONTACT] ?? {};

  const tourCalendarRoute = getTourCalendarRoute({ listingAlias });

  const { availabilityDates, selectedBookingMethod, selectedTimeZone, selectedTourDuration, tourCalendarApiHook } =
    useTourSchedulingContext();
  const isTouringSchedulingEnabled = Boolean(selectedBookingMethod && selectedTimeZone);

  const handleEditBookingMethodClick = () => {
    if (selectedBookingMethod) {
      return onClickEditTourSection(EditListingTourConfigSteps.EDIT_BOOKING_METHOD);
    }

    return onClickEditTourSection(EditListingTourConfigSteps.SET_BOOKING_METHOD);
  };

  const handleEditAvailabilityClick = () => {
    return onClickEditTourSection(EditListingTourConfigSteps.EDIT_AVAILABILITY);
  };

  const handleEditTourDurationClick = () => {
    return onClickEditTourSection(EditListingTourConfigSteps.EDIT_TOUR_DURATION);
  };

  useEffect(() => {
    if (tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading) {
      return;
    }

    if (listingDetails && user) {
      emitReviewStepBookingMethodInitialStateImpressionEvent({
        currentProperty: listingDetails,
        user,
        isActive: active,
        tourConfirmationType: tourCalendarApiHook.tourConfirmationType,
      });
    }
  }, [tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading]);

  return (
    <>
      <Box css={reviewSectionStyles} data-testid="TouringSection-TourType">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {recommendations[RECS.SCHEDULE] && (
                <RecommendationIconBulb
                  fieldName={TOUR_BOOKING_METHOD}
                  removeFlex={true}
                  onClick={() => {
                    onClickRecommendation(RECS.SCHEDULE);
                  }}
                />
              )}
            </Box>

            <Box css={reviewSectionTitleStyles}>
              <Text css={{ textStyle: 'bodyHeading' }}>Tour booking method</Text>

              {!isTouringSchedulingEnabled && !active && (
                <TextButton
                  aria-label={`learn-more-about-${TOUR_BOOKING_METHOD}`}
                  data-testid="TouringSection-LearnMore"
                  textStyle="body"
                  onClick={() => {
                    onClickRecommendation(RECS.SCHEDULE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
              {active && (
                <TextButton
                  asChild
                  aria-label={`learn-more-about-${TOUR_BOOKING_METHOD}`}
                  data-testid="TouringSection-CalendarLink"
                  textStyle="body"
                >
                  <Link href={tourCalendarRoute}>View calendar to edit</Link>
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            onEditClick={handleEditBookingMethodClick}
            useLockIcon={active}
            disabledMessage="To change your booking method while your listing is live, go to your booking calendar."
            dataEventId={EVENT_IDS.REVIEW_STEP_EDIT_BOOKING_METHOD_CLICK}
          />
        </Box>
        <FieldValueDisplay value={selectedBookingMethod ? DisplayTourTypes[selectedBookingMethod] : 'Not set'} />
      </Box>

      {isTouringSchedulingEnabled && (
        <>
          <Box
            css={reviewSectionStyles}
            data-testid="TouringSection-TourDuration"
            data-eventid={EVENT_IDS.REVIEW_STEP_TOUR_DURATION_IMPRESSION}
          >
            <Box css={reviewTitleRowStyles}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Box css={iconPlaceholderStyles} />
                <Text textStyle="body-bold">Length of each tour</Text>
              </Box>
              <ContentSectionEditButton
                onEditClick={handleEditTourDurationClick}
                useLockIcon={active}
                disabledMessage="To change tour length while your listing is live, go to your booking calendar."
                dataEventId={EVENT_IDS.REVIEW_STEP_TOUR_DURATION_CLICK}
              />
            </Box>
            <FieldValueDisplay
              value={selectedTourDuration ? TOUR_DURATION_DISPLAY_MAP[selectedTourDuration] : 'Not set'}
            />
          </Box>

          <Box css={reviewSectionStyles} data-testid="TouringSection-TimeZone">
            <Box css={reviewTitleRowStyles}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Box css={iconPlaceholderStyles} />
                <Text css={{ textStyle: 'bodyHeading' }}>Time zone of the property</Text>
              </Box>
            </Box>
            <FieldValueDisplay value={DisplayTimeZones[selectedTimeZone as TimeZoneIds]} />
          </Box>

          <Box
            css={reviewSectionStyles}
            data-testid="TouringSection-Availability"
            data-eventid={EVENT_IDS.REVIEW_STEP_EDIT_AVAILABILITY_IMPRESSION}
          >
            <Box css={reviewTitleRowStyles}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Box css={iconPlaceholderStyles}>
                  {isTouringSchedulingEnabled && !availabilityDates.length && (
                    <AccordionIconError tabIndex={0} title="Tour availability incomplete error" marginRight="xs" />
                  )}
                </Box>
                <Text css={{ textStyle: 'bodyHeading' }}>Tour availability</Text>
              </Box>

              <ContentSectionEditButton
                onEditClick={handleEditAvailabilityClick}
                useLockIcon={active}
                disabledMessage="To add tour availability while your listing is live, go to your booking calendar."
                dataEventId={EVENT_IDS.REVIEW_STEP_EDIT_AVAILABILITY_CLICK}
              />
            </Box>
            <FieldValueDisplay customDisplayComponent={<TouringAvailability availabilityDates={availabilityDates} />} />
          </Box>
        </>
      )}
    </>
  );
};

export default TouringSection;
